import { createBrowserHistory } from 'history';
import { observable, toJS } from 'mobx';
import bridge from './bridge';
export const history = createBrowserHistory();

export const PATH_PREFIX = '/client';

export enum VPathMethod {
  PUSH = 'PUSH',
  REPLACE = 'REPLACE',
}

export enum VPathKey {
  COMPLETE = 'COMPLETE',
  CHATTING_ROOM = 'CHATTING_ROOM',
  WAITING_ROOM = 'WAITING_ROOM',
  CLIENT_INIT = 'CLIENT_INIT',
  CLIENT_OUTBOUND_INIT = 'CLIENT_OUTBOUND_INIT',
  CHECK = 'CHECK',
  DEMO = 'DEMO',
  DEMO_WITH_ACCESS_KEY = 'DEMO_WITH_ACCESS_KEY',
  PRECALL = 'PRECALL',
  TEST_OUTBOUND = 'TEST_OUTBOUND',
  TEST_TRANSLATION = 'TEST_TRANSLATION',
  TEST_COMPONENTS = 'TEST_COMPONENTS',

  // india
  CONSENT = 'CONSENT',
  PAN_CARD = 'PAN_CARD',
  PAN_INFO = 'PAN_INFO',
  XML_UPLOAD = 'XML_UPLOAD',

  // indo
  KTP_CARD = 'KTP_CARD',
  KTP_INFO = 'KTP_INFO',

  // global
  CARD_SELECT = 'CARD_SELECT',
  CARD_UPLOAD = 'CARD_UPLOAD',
  INFO_SUBMIT = 'INFO_SUBMIT',
}

const vPath = {
  COMPLETE: '/complete',
  CHATTING_ROOM: '/chatting-room',
  WAITING_ROOM: '/waiting-room',
  CLIENT_INIT: '', // no use
  CLIENT_OUTBOUND_INIT: '/outbound',
  CHECK: '/check',
  DEMO: '/demo',
  DEMO_WITH_ACCESS_KEY: '/demo/:key',
  PRECALL: '/precall',
  TEST_OUTBOUND: '/test/outbound',
  TEST_TRANSLATION: '/test/translation',
  TEST_COMPONENTS: '/test/components',

  // india
  CONSENT: '/consent',
  PAN_CARD: '/pan-card',
  PAN_INFO: '/pan-info',
  XML_UPLOAD: '/xml-upload',

  // indo
  KTP_CARD: '/ktp-card',
  KTP_INFO: '/ktp-info',

  // global
  CARD_SELECT: '/card-select',
  CARD_UPLOAD: '/card-upload',
  INFO_SUBMIT: '/info-submit',
};

const hasNavPathKeys = [
  VPathKey.CONSENT,
  VPathKey.XML_UPLOAD,
  VPathKey.PAN_CARD,
  VPathKey.PAN_INFO,
  VPathKey.KTP_CARD,
  VPathKey.KTP_INFO,
  VPathKey.CARD_SELECT,
  VPathKey.CARD_UPLOAD,
  VPathKey.INFO_SUBMIT,
];

const cannotGoBack = [VPathKey.CONSENT];

class VHistory {
  @observable
  private currentPathKey = '';

  public getCurrentPathKey() {
    return this.currentPathKey;
  }

  public cannotCurrentGoBack() {
    try {
      return cannotGoBack.includes(this.currentPathKey as VPathKey);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  public ifShowNav() {
    try {
      return hasNavPathKeys.includes(this.currentPathKey as VPathKey);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  public isDemoPage() {
    return this.currentPathKey === VPathKey.DEMO;
  }

  public updateCurrentPathKey = (key: string) => {
    this.currentPathKey = key;
  };

  public toPageByKey = (key: string, query?: string) => {
    const queryStr = query || '';
    if (vPath.hasOwnProperty(key)) {
      history.push(PATH_PREFIX + vPath[key as VPathKey] + queryStr);
    } else {
      throw new Error('no such path, please check lib/history.ts');
    }
  };

  public replacePageByKey = (key: string, query?: string) => {
    const queryStr = query || '';
    if (vPath.hasOwnProperty(key)) {
      history.replace(PATH_PREFIX + vPath[key as VPathKey] + queryStr);
    } else {
      throw new Error('no such path, please check lib/history.ts');
    }
  };

  public getKeyThroughPathname = (pathname: string) => {
    for (let key in vPath) {
      if (vPath.hasOwnProperty(key) && this.getPathnameByKey(key) === pathname) {
        return key;
      }
    }
    throw new Error('no such path');
  };

  public getPathnameByKey = (key: string) => {
    if (vPath.hasOwnProperty(key)) {
      return PATH_PREFIX + vPath[key as VPathKey];
    } else {
      throw new Error('no such path');
    }
  };

  public currentPathIs = (key: VPathKey) => {
    return location.pathname === this.getPathnameByKey(key);
  };
}

const vHistory = new VHistory();

export default vHistory;

['development', 'staging'].includes(process.env.MODE!) && Object.assign(window, { vHistory, toJS });
